// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import { faro } from '@grafana/faro-web-sdk';
import { CypressFields } from '../../utils/Cypress';
import { Product } from '../../protos/demo';
import ProductPrice from '../ProductPrice';
import * as S from './ProductCard.styled';
import { useState, useEffect, MouseEvent } from 'react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { getImageWithDelay } from '../../utils/DelayedImage';
import SessionGateway from '../../gateways/Session.gateway';
import { useCart } from '../../providers/Cart.provider';
import { getCompareAtPrice, getProductMeta } from '../../utils/ProductMeta';

interface IProps {
  product: Product;
}

const ProductCard = ({
  product: {
    id,
    picture,
    name,
    categories = [],
    priceUsd = {
      currencyCode: 'USD',
      units: 0,
      nanos: 0,
    },
  },
}: IProps) => {

  const { geography, device } = SessionGateway.getSession();
  const slowLoadEnabled = useBooleanFlagValue('imageSlowLoad', false);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [justAdded, setJustAdded] = useState(false);
  const { addItem, openDrawer } = useCart();

  useEffect(() => {
    let objectUrl = '';
    let cancelled = false;
    if(picture){
      getImageWithDelay(picture, slowLoadEnabled).then(blob => {
        if (cancelled) {
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setImageSrc(objectUrl);
      });
    }
    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [picture, slowLoadEnabled]);

  useEffect(() => {
    if (!justAdded) {
      return;
    }
    const timer = setTimeout(() => setJustAdded(false), 1600);
    return () => clearTimeout(timer);
  }, [justAdded]);

  const { rating, reviewCount, badge } = getProductMeta(id);

  const onQuickAdd = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    faro.api.startUserAction('add-product-to-cart', {
      type: 'interaction',
      productId: id,
      device: device,
      geography: geography
    });
    addItem({ productId: id, quantity: 1 })
      .then(() => setJustAdded(true))
      .catch(() => undefined)
      .finally(() => openDrawer());
  };

  return (
    <S.ProductCard data-cy={CypressFields.ProductCard}>
      <S.CardLink href={`/product/${id}?device=${device}&geography=${geography}`} aria-label={`View ${name}`} />
      <S.ImageWrap>
          <S.Image $src={imageSrc} />
          {badge && <S.Badge $variant={badge}>{badge}</S.Badge>}
          <S.QuickAdd onClick={onQuickAdd} disabled={justAdded} aria-label={`Add ${name} to cart`} title="Add to cart">
            {justAdded ? (
              '✓'
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 7h12l1 14H5L6 7z" />
                <path d="M9 10V6a3 3 0 0 1 6 0v4" />
                <path d="M12 13v5M9.5 15.5h5" />
              </svg>
            )}
          </S.QuickAdd>
        </S.ImageWrap>
        <S.Content>
          {categories.length > 0 && <S.Category>{categories[0]}</S.Category>}
          <S.ProductName>{name}</S.ProductName>
          <S.Rating aria-label={`Rated ${rating.toFixed(1)} out of 5 from ${reviewCount} reviews`}>
            <S.Stars>
              {Array.from({ length: 5 }, (_, index) => (
                <S.Star key={index} $filled={index < Math.round(rating)}>
                  ★
                </S.Star>
              ))}
            </S.Stars>
            <span>({reviewCount})</span>
          </S.Rating>
          <S.ProductPrice>
            <ProductPrice price={priceUsd} />
            {badge === 'Sale' && (
              <s>
                <ProductPrice price={getCompareAtPrice(priceUsd)} />
              </s>
            )}
          </S.ProductPrice>
        </S.Content>
    </S.ProductCard>
  );
};

export default ProductCard;
